import React, {useEffect, useState} from 'react';
import styled from "styled-components";
import {useAuth} from "../auth/AuthProvider";
import AuthResponse from "../models/AuthResponse";
import {useAxios} from "../AxiosProvider";
import {AxiosError, AxiosResponse} from "axios";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";
import {Input} from "./form/FormElements";
import {useMessageService} from "../hooks/useMessageService";
import {Form, Label, PrimaryButton, primaryButtonStyles} from "../styledcomponents/FormStyledComponents";
import {useNavigate} from "react-router-dom";
import {ResponseCode} from "../enums/ResponseCode";
import {Page, usePath} from "../PathProvider";
import {InlineLink} from '../styledcomponents/MiscStyledComponents';
import ErrorUtils from "../utils/errorUtils";
import {ButtonBar, ButtonBarButton} from "./misc/ButtonBar";
import {UserType} from "../enums/UserType";
import ExternalLink from "./misc/ExternalLink";
import GetExternalFeUrl, { getB2cUrl, URLSUFFIX_CUSTOMER_REGISTER, URLSUFFIX_WHOLESALE } from '../utils/externalUrl';
import { LoginB2cLoading } from './LoginB2cLoading';

const LoginFormWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: .5rem;
`

const LoginWrapper = styled.div`
    display: grid;
    width: 100%;
    gap: 3rem;
    grid-auto-flow: column;
    @media (max-width: 768px) {
      grid-auto-flow: row;
    }
`

const LoginWidget = styled.div`
    display: grid;
    border: 2px solid #D2D2D2;
    border-radius: 6px;
    grid-template-areas:
            "buttonBar buttonBar"
            "loginForm loginForm"
    ;
`

const LoginFormContainer = styled.div`
display: grid;
grid-template-columns: repeat(2,1fr);
gap: 1rem;
`

const LoginText = styled.div`
    padding: 30px;
`

const LoginTitle = styled.div`
    font-weight: 700;
    font-size: 20px;
    color: #00477D;
    margin-bottom: 1rem;
`
const AccountLogInContent = styled.div`
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    padding-top: 10px;
`
const BlueBackground = styled.div`
    background: #E6F2FA;
    padding: 30px;
`

const SsoButton = styled.div`
    margin-top: 2em;
    display: flex;
`

const SsoButtonLink = styled.a`
    ${primaryButtonStyles}
    margin: auto;
    padding: 1em;
    width: 20em;
`

type LoginFormData = {
    username: string,
    password: string,
};

function LoginForm() {
    // Hooks
    const auth = useAuth();
    const navigate = useNavigate();
    const axios = useAxios();
    const messageService = useMessageService();
    const path = usePath();
    const [screenWidth, setScreenWidth]   = useState(window.innerWidth);

    // useForm hooks
    const methods = useForm<LoginFormData>({});
    const { handleSubmit, formState: { isSubmitting, isDirty } } = methods;

    // Form state handlers
    const [loginDisabled, setLoginDisabled] = useState<boolean>(true);
    const [loginType, setLoginType] = useState<UserType>(UserType.Vendor);
    const [loadingB2c, setLoadingB2c] = useState<boolean>(false);

    // useEffects
    useEffect(() => {
        setLoginDisabled(isSubmitting || !isDirty);
    }, [isSubmitting, isDirty]);

    useEffect(() => { //Tracking screen width, so we can omit "Retail" on the login buttons if the screen is small
        const resize = () => setScreenWidth(window.innerWidth);
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    // Functions
	const sendHash = async (data: string) => {
		data = data.replace('#id_token=','');
        // clear any messages once we submit
        messageService.clearAll();

        await axios?.openApi.get(
            '/api/logintoken?token=' + data
        ).then((response: AxiosResponse) => {
            const authResponse: AuthResponse = response.data;
            authResponse.isVendor = false;
            authResponse.isCustomer = true;

            auth.login(authResponse);
        }).catch((err: AxiosError) => {
            console.error(err);

            let errorResponse: AxiosResponse | undefined = err.response;

            switch (errorResponse?.status) {
                case 400:
                    let response = Object.values(errorResponse?.data).join("\n");
                    messageService.error(response);
                    break;
                case 409:
                    console.log(errorResponse);
                    switch (errorResponse?.data.message) {
                        case ResponseCode.AccountLocked:
                            navigate(path.get(Page.ForgotCredentials), { state: { warningMessage: 'Account locked. Please use Reset Password to unlock and reset your password.' } });
                            break;
                        case ResponseCode.PasswordExpired:
                            navigate(path.get(Page.ForgotCredentials), { state: { warningMessage: 'Password expired. Please use Reset Password to set a new password.' } });
                            break;
                        case ResponseCode.ForcePasswordReset:
                            navigate(path.get(Page.ForcedResetPassword), { state: { warningMessage: 'Password expired. Please change your password.' } });
                            break;
                        default:
                            messageService.error(ErrorUtils.UNAUTHORIZED_ERROR);
                    }
                    break;
                default:
                    messageService.error(ErrorUtils.UNKNOWN_ERROR);
            }
        });
	}

	if(window.location.hash.includes('#id_token') && !loadingB2c){
		let idToken = window.location.hash;
		setLoadingB2c(true);
		sendHash(idToken);
	}

    const onSubmit: SubmitHandler<LoginFormData> = async (data: LoginFormData) => {
        // clear any messages once we submit
        messageService.clearAll();

        await axios?.openApi.post(
            '/api/login',
            { username: data.username, password: data.password, userType: loginType }
        ).then((response: AxiosResponse) => {
            const authResponse: AuthResponse = response.data;
            authResponse.isVendor = true;
            authResponse.isCustomer = false;

            auth.login(authResponse);
        }).catch((err: AxiosError) => {
            console.error(err);

            let errorResponse: AxiosResponse | undefined = err.response;

            switch (errorResponse?.status) {
                case 400:
                    let response = Object.values(errorResponse?.data).join("\n");
                    messageService.error(response);
                    break;
                case 409:
                    console.log(errorResponse);
                    switch (errorResponse?.data.message) {
                        case ResponseCode.AccountLocked:
                            navigate(path.get(Page.ForgotCredentials), { state: { warningMessage: 'Account locked. Please use Reset Password to unlock and reset your password.' } });
                            break;
                        case ResponseCode.PasswordExpired:
                            navigate(path.get(Page.ForgotCredentials), { state: { warningMessage: 'Password expired. Please use Reset Password to set a new password.' } });
                            break;
                        case ResponseCode.ForcePasswordReset:
                            navigate(path.get(Page.ForcedResetPassword), { state: { warningMessage: 'Password expired. Please change your password.' } });
                            break;
                        default:
                            messageService.error(ErrorUtils.UNAUTHORIZED_ERROR);
                    }
                    break;
                default:
                    messageService.error(ErrorUtils.UNKNOWN_ERROR);
            }
        });
    };

	let urlB2C = "";
	if(window.location.port.match(/300/g)){
        // for localhost:3000, use b2cdevlogin
		urlB2C = "https://b2cdevlogin.firstenergycorp.com/551ca087-06a5-4de8-abfd-7752f5d91ce3/oauth2/v2.0/authorize?p=B2C_1A_SIGNIN&client_id=71a3e71b-643b-4d06-b378-297becb50bf1&nonce=defaultNonce&redirect_uri=http%3A%2F%2Flocalhost%3A3000%2Flogin&scope=openid&response_type=id_token&prompt=login";
	} else {
        urlB2C = getB2cUrl()

    }

    const retailPrefix = (screenWidth > 600) ? 'Retail ' : '';
    if (loadingB2c) {
        return <LoginB2cLoading/>
    }

    const vendorLogin = <BlueBackground>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Label htmlFor={"username"}>Username</Label>
                <Input name={"username"} />
                <Label htmlFor={"password"}>Password</Label>
                <Input type="password" name={"password"} autoComplete={'off'}/>
                <PrimaryButton type={"submit"} disabled={loginDisabled} float='left'>
                    {isSubmitting ? 'Logging in...' : 'LOG IN'}
                </PrimaryButton>
            </Form>
        </BlueBackground>

    const customerLogin = <BlueBackground>
            <SsoButton>
                <SsoButtonLink href={urlB2C}>
                        Log in
                </SsoButtonLink>
            </SsoButton>
    </BlueBackground>
    return (
        <LoginFormWrapper>
            <FormProvider {...methods} >
            
                <p style={{marginTop:'0px'}}>
                    The Interconnection Portal is available for retail customers and contractors/installers to log in and electronically create an application,
                    attach documents, and review drafted and submitted applications.
                </p>
                <LoginWidget>
                    <ButtonBar style={{ gridArea: 'buttonBar' }}>
                        <ButtonBarButton active={loginType === UserType.Vendor} onClick={() => setLoginType(UserType.Vendor)}>{retailPrefix} Contractor/Installer</ButtonBarButton>
                        <ButtonBarButton active={loginType === UserType.Customer} onClick={() => setLoginType(UserType.Customer)}>{retailPrefix} Customer</ButtonBarButton>
                    </ButtonBar>
                    <LoginFormContainer style={{ gridArea: 'loginForm' }}>

                        <LoginText >
                            <LoginTitle>
                                {
                                    loginType === UserType.Customer
                                        ? 'Customer Account Log In'
                                        : 'Contractor/Installer Account Log In'
                                }
                            </LoginTitle>
                            <div>
                                Log in to your account to access the Interconnection Portal.
                                <AccountLogInContent>
                                    {
                                        loginType === UserType.Customer
                                            ? <ExternalLink href={urlB2C}>Forgot Username or Password?</ExternalLink>
                                            : <InlineLink to={path.get(Page.ForgotCredentials)}>Forgot Username or Password?</InlineLink>
                                    }
                                </AccountLogInContent>
                                <div>
                                    <AccountLogInContent>
                                        Don't have an online account?
                                        {
                                            loginType === UserType.Customer
                                                ? <ExternalLink href={GetExternalFeUrl(URLSUFFIX_CUSTOMER_REGISTER)}> Register Here</ExternalLink>
                                                : <InlineLink to={path.get(Page.Registration)}> Register Here</InlineLink>
                                        }
                                    </AccountLogInContent>
                                </div>
                            </div>
                        </LoginText>
                        {loginType === UserType.Customer ? customerLogin : vendorLogin}

                    </LoginFormContainer>

                </LoginWidget>
                <h3 style={{marginBottom:"0em", marginTop:"1.5em"}}>Wholesale - Preapplication - PURPA</h3>
                <p style={{marginTop:"0em"}}>
                    To submit an application as a wholesale customer who will sell electricity through the wholesale PJM market, 
                    please visit <ExternalLink href={GetExternalFeUrl(URLSUFFIX_WHOLESALE)}>Wholesale/PURPA</ExternalLink> for more information.
                </p>
            </FormProvider>
        </LoginFormWrapper>
    )
}

function Login() {
    const auth = useAuth();
    if (auth.user) { auth.logout(); }

    return (
        <LoginWrapper>
            <LoginForm />
        </LoginWrapper>
    )
}

export default Login;